import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Palette, Save, Crown, Lock, Mail, Phone, Globe2, Trash2, Loader2,
  Plus, Users, ArrowRight, Building2, ExternalLink, Globe, Check, AlertCircle, Copy,
} from "lucide-react";

const DEFAULTS = {
  brand_name: "", primary_color: "#6366F1", accent_color: "#8B5CF6",
  logo_url: "", favicon_url: "", support_email: "",
  support_phone: "", custom_domain: "", footer_text: "", powered_by_visible: true,
};

export default function WhiteLabel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  const [clients, setClients] = useState([]);
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientLabel, setNewClientLabel] = useState("");
  const [addingClient, setAddingClient] = useState(false);

  const load = async () => {
    const { data } = await api.get("/whitelabel/me");
    setData(data);
    if (data.profile) {
      setForm({ ...DEFAULTS, ...data.profile });
    }
    if (data.eligible) {
      const { data: cls } = await api.get("/whitelabel/clients");
      setClients(cls);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.brand_name.trim()) { toast.error("Brand name required"); return; }
    setSaving(true);
    try {
      await api.put("/whitelabel/me", form);
      toast.success("White-label profile saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const removeProfile = async () => {
    if (!window.confirm("Delete white-label profile? Client links will also be removed.")) return;
    await api.delete("/whitelabel/me");
    toast.success("Profile deleted");
    setForm(DEFAULTS); load();
  };

  const addClient = async () => {
    if (!newClientEmail) return;
    setAddingClient(true);
    try {
      await api.post("/whitelabel/clients", { client_email: newClientEmail, label: newClientLabel || null });
      setNewClientEmail(""); setNewClientLabel("");
      toast.success("Client added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setAddingClient(false); }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Remove this client link?")) return;
    await api.delete(`/whitelabel/clients/${id}`);
    toast.success("Removed");
    load();
  };

  if (!data) return <div className="h-64 glass rounded-2xl animate-pulse" />;

  const blocked = !data.eligible;

  return (
    <div className="space-y-8" data-testid="whitelabel-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Crown className="h-3.5 w-3.5" /> White-label · Agency & Enterprise
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Your brand. Your platform.</h1>
        <p className="text-white/50 mt-3 max-w-2xl text-sm">
          Re-skin shared pages, public blog, and the share-logo link with your agency's branding. Add sub-clients to manage their workspaces from your dashboard.
        </p>
      </header>

      {blocked && (
        <div className="glass rounded-2xl p-6 border border-amber-500/30 bg-amber-500/[0.04]" data-testid="whitelabel-upgrade-banner">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-white/80">
              <span className="font-semibold text-amber-400">Upgrade required.</span> White-label is included in the <strong>Agency</strong> (₹50,000) and <strong>Enterprise</strong> (₹1,50,000) plans. Your current plan: <span className="font-mono">{data.plan}</span>.
              <a href="/dashboard/billing" className="ml-2 text-indigo-300 underline">Upgrade now →</a>
            </div>
          </div>
        </div>
      )}

      <section className={`glass rounded-2xl p-6 lg:p-8 ${blocked ? "opacity-60 pointer-events-none" : ""}`}>
        <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
          <Palette className="h-5 w-5 text-indigo-400" /> Brand profile
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Brand name *">
            <input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              data-testid="wl-brand-name"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Custom domain (DNS-mapped)">
            <input value={form.custom_domain || ""} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
              placeholder="agency.yourdomain.com" data-testid="wl-domain"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Logo URL">
            <input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://..." data-testid="wl-logo"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Favicon URL">
            <input value={form.favicon_url || ""} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })}
              placeholder="https://..." data-testid="wl-favicon"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Primary color">
            <div className="flex gap-2">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="h-10 w-12 rounded border border-white/10 bg-transparent" data-testid="wl-primary" />
              <input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
          </Field>
          <Field label="Accent color">
            <div className="flex gap-2">
              <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="h-10 w-12 rounded border border-white/10 bg-transparent" data-testid="wl-accent" />
              <input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
            </div>
          </Field>
          <Field label="Support email">
            <input type="email" value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })}
              data-testid="wl-email"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Support phone">
            <input value={form.support_phone || ""} onChange={(e) => setForm({ ...form, support_phone: e.target.value })}
              data-testid="wl-phone"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Footer text" full>
            <input value={form.footer_text || ""} onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
              placeholder={`© ${new Date().getFullYear()} ${form.brand_name || "Your Agency"}. All rights reserved.`}
              data-testid="wl-footer"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Field>
        </div>

        <label className="mt-4 inline-flex items-center gap-2 text-sm text-white/60">
          <input type="checkbox" checked={!!form.powered_by_visible}
            onChange={(e) => setForm({ ...form, powered_by_visible: e.target.checked })}
            data-testid="wl-powered" />
          Show "Powered by MakeYourBrand.IN" badge on public pages
        </label>

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={save} disabled={saving} data-testid="wl-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
          </button>
          {data.profile && (
            <button onClick={removeProfile} data-testid="wl-delete-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg glass text-sm font-semibold text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-4 w-4" /> Delete profile
            </button>
          )}
        </div>

        {data.profile?.slug && (
          <div className="mt-5 glass rounded-lg p-3 text-xs">
            <div className="text-white/40 uppercase tracking-[0.2em] mb-1">Public brand URL slug</div>
            <code className="text-indigo-300 break-all" data-testid="wl-slug">{process.env.REACT_APP_BACKEND_URL}/api/public/brand/{data.profile.slug}</code>
          </div>
        )}
      </section>

      {!blocked && <DomainPanel profile={data.profile} reload={load} />}

      {!blocked && (
        <section className="glass rounded-2xl p-6 lg:p-8" data-testid="wl-clients-section">
          <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-400" /> Sub-clients · {clients.length}
          </h3>
          <p className="text-sm text-white/50 mb-4">
            Add existing MakeYourBrand.IN users as your clients to see their usage at a glance. Each client must have created a free account first.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            <input value={newClientEmail} onChange={(e) => setNewClientEmail(e.target.value)}
              placeholder="client@email.com" data-testid="wl-new-client-email"
              className="flex-1 min-w-[200px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <input value={newClientLabel} onChange={(e) => setNewClientLabel(e.target.value)}
              placeholder="Label / nickname" data-testid="wl-new-client-label"
              className="flex-1 min-w-[160px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <button onClick={addClient} disabled={addingClient} data-testid="wl-add-client-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {addingClient ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </button>
          </div>
          {clients.length === 0 ? (
            <div className="text-sm text-white/40 text-center py-8 border border-dashed border-white/10 rounded-lg">
              No clients added yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {clients.map((c) => (
                <div key={c.id} className="glass rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`wl-client-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{c.client_name || c.client_email}</div>
                    {c.label && <div className="text-xs text-white/40">{c.label}</div>}
                    <div className="text-xs text-white/40 mt-1 inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.client_email}</div>
                    {c.business?.business_name && (
                      <div className="text-xs text-indigo-300 mt-1 inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3" />{c.business.business_name}
                      </div>
                    )}
                    <div className="flex gap-3 mt-2 text-xs text-white/50 font-mono">
                      <span>{c.stats.content_count} content</span>
                      <span>{c.stats.crm_leads} leads</span>
                    </div>
                  </div>
                  <button onClick={() => removeClient(c.id)} data-testid={`wl-remove-client-${c.id}`}
                    className="p-2 rounded hover:bg-rose-500/10"><Trash2 className="h-4 w-4 text-rose-300" /></button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DomainPanel({ profile, reload }) {
  const [domain, setDomain] = useState(profile?.custom_domain || "");
  const [challenge, setChallenge] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState(null);

  const verified = !!profile?.domain_verified;

  const issue = async () => {
    if (!domain.trim()) { toast.error("Domain required"); return; }
    setIssuing(true);
    try {
      const { data } = await api.post("/whitelabel/domain/issue", { custom_domain: domain.trim() });
      setChallenge(data);
      setResult(null);
      toast.success("DNS challenge issued. Add the TXT record to your DNS, then click Verify.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setIssuing(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/whitelabel/domain/verify");
      setResult(data);
      if (data.verified) {
        toast.success("Domain verified ✔");
        reload();
      } else {
        toast.error("Not verified yet. DNS may take a few minutes to propagate.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setVerifying(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the custom domain? Tenant routing for this domain will stop working.")) return;
    await api.delete("/whitelabel/domain");
    setChallenge(null); setResult(null); setDomain("");
    toast.success("Custom domain cleared");
    reload();
  };

  const copy = async (v) => {
    await navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  return (
    <section className="glass rounded-2xl p-6 lg:p-8" data-testid="wl-domain-section">
      <h3 className="font-heading text-xl font-black mb-4 flex items-center gap-2">
        <Globe className="h-5 w-5 text-indigo-400" /> Custom domain
        {verified && (
          <span className="inline-flex items-center gap-1 text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full">
            <Check className="h-3 w-3" /> Verified
          </span>
        )}
      </h3>
      <p className="text-sm text-white/50 mb-4">
        Serve the platform under your own domain (e.g. <code className="text-indigo-300">app.youragency.com</code>). 
        Once verified, public share pages, blogs and OG cards render with your brand when accessed via this hostname.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={domain} onChange={(e) => setDomain(e.target.value)}
          placeholder="app.youragency.com"
          data-testid="wl-domain-input"
          className="flex-1 min-w-[240px] bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
        />
        <button onClick={issue} disabled={issuing} data-testid="wl-domain-issue"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          {profile?.verification_token ? "Re-issue challenge" : "Issue DNS challenge"}
        </button>
      </div>

      {challenge && (
        <div className="glass rounded-xl p-4 mb-3 border border-indigo-500/30" data-testid="wl-domain-challenge">
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-2">DNS records to add</div>
          <div className="space-y-2 text-xs font-mono">
            <RecordRow label="Type" value={challenge.record_type} onCopy={copy} />
            <RecordRow label="Host" value={challenge.record_host} onCopy={copy} />
            <RecordRow label="Value" value={challenge.record_value} onCopy={copy} />
            <RecordRow label="TTL" value={challenge.ttl_seconds + " seconds"} onCopy={copy} />
          </div>
          <div className="mt-3 text-xs text-white/50">
            Also point traffic via a CNAME from <code className="text-indigo-300">{challenge.domain}</code> →{" "}
            <code className="text-indigo-300">{challenge.cname_target}</code> (or A-record to the platform IP).
          </div>
          <div className="mt-3 text-xs text-white/50">
            For HTTPS / SSL: use Cloudflare proxied DNS (free), or run a Caddy reverse-proxy that issues
            a Let's Encrypt cert on demand for whitelisted hostnames. Currently SSL provisioning is documentation-only.
          </div>
        </div>
      )}

      {profile?.verification_token && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={verify} disabled={verifying} data-testid="wl-domain-verify"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg glass text-sm font-semibold hover:bg-white/5 disabled:opacity-50">
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Verify TXT record
          </button>
          {profile?.custom_domain && (
            <button onClick={clear} data-testid="wl-domain-clear"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg glass text-sm text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-4 w-4" /> Remove domain
            </button>
          )}
        </div>
      )}

      {result && !result.verified && (
        <div className="glass rounded-lg p-3 text-xs border border-amber-500/30 bg-amber-500/[0.04]" data-testid="wl-domain-debug">
          <div className="text-amber-300 flex items-center gap-2 mb-1"><AlertCircle className="h-4 w-4" /> Not yet verified</div>
          <div className="text-white/60">
            Queried: <code className="text-indigo-300">{result.queried}</code>
          </div>
          <div className="text-white/60">
            Expected TXT: <code className="text-indigo-300">{result.expected}</code>
          </div>
          <div className="text-white/60">
            Records found: {result.records_found.length === 0 ? <em className="text-white/40">none</em> : result.records_found.map((r, i) => <code key={i} className="text-indigo-300 mr-2">{r}</code>)}
          </div>
        </div>
      )}

      {verified && <SSLPanel />}
    </section>
  );
}

function SSLPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/whitelabel/ssl/status");
      setData(r.data);
    } catch { /* swallow */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const provision = async () => {
    setProvisioning(true);
    try {
      await api.post("/whitelabel/ssl/provision");
      toast.success("SSL provisioning requested. Polling Cloudflare…");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provisioning failed");
    } finally { setProvisioning(false); }
  };

  if (loading || !data) return null;
  const ssl = data.ssl_provisioning;

  return (
    <div className="mt-5 glass rounded-xl p-4 border border-emerald-500/20" data-testid="wl-ssl-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="font-heading font-black flex items-center gap-2">
          <Globe className="h-4 w-4 text-emerald-400" /> SSL · Cloudflare for SaaS
          {ssl?.ready && (
            <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded-full inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> Active
            </span>
          )}
        </div>
        {data.cloudflare_configured ? (
          <button onClick={provision} disabled={provisioning} data-testid="wl-ssl-provision"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg glass text-xs hover:bg-white/5 disabled:opacity-50">
            {provisioning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Globe className="h-3 w-3" />}
            {ssl ? "Refresh status" : "Provision SSL"}
          </button>
        ) : (
          <span className="text-xs text-amber-300">Platform admin must configure Cloudflare first</span>
        )}
      </div>
      {ssl ? (
        <div className="text-xs space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <div><span className="text-white/40">Hostname status:</span> <code className="text-indigo-300">{ssl.status || "—"}</code></div>
            <div><span className="text-white/40">SSL status:</span> <code className="text-indigo-300">{ssl.ssl_status || "—"}</code></div>
          </div>
          {(ssl.validation_records || []).length > 0 && (
            <div className="glass rounded-lg p-2 mt-2">
              <div className="text-white/40 uppercase tracking-[0.2em] text-[10px] mb-1">Additional DV records (if requested by Cloudflare)</div>
              {ssl.validation_records.map((vr, i) => (
                <pre key={i} className="text-[10px] text-indigo-300 break-all whitespace-pre-wrap">{JSON.stringify(vr, null, 2)}</pre>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-white/50">
          {data.cloudflare_configured
            ? "Click Provision SSL to register this domain with Cloudflare for SaaS."
            : "Once the admin configures Cloudflare, automatic SSL will provision on next verify."}
        </div>
      )}
    </div>
  );
}

function RecordRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-white/40 w-20 uppercase tracking-[0.2em]">{label}</span>
      <code className="flex-1 text-indigo-300 break-all">{value}</code>
      <button onClick={() => onCopy(value)} className="p-1.5 rounded hover:bg-white/5">
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}
